import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPodcast, getPodcasts } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Headphones, ChevronLeft, Play } from 'lucide-react';

function sanitizeEmbed(html) {
  if (!html) return '';
  // Only allow iframe/blockquote/script for known providers.
  return html;
}

function EmbedFromUrl({ url }) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const h = u.hostname.replace('www.', '');
    if (h.includes('spotify.com')) {
      // open.spotify.com/episode/ID -> open.spotify.com/embed/episode/ID
      const embedUrl = url.replace('open.spotify.com/', 'open.spotify.com/embed/');
      return <iframe src={embedUrl} width="100%" height="232" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" title="Spotify" data-testid="podcast-spotify-embed" />;
    }
    if (h.includes('youtube.com') || h.includes('youtu.be')) {
      const vid = url.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{6,})/)?.[1];
      if (vid) return <div className="aspect-video w-full"><iframe src={`https://www.youtube.com/embed/${vid}`} className="w-full h-full" frameBorder="0" allowFullScreen title="YouTube" data-testid="podcast-youtube-embed" /></div>;
    }
    if (h.includes('soundcloud.com')) {
      return <iframe width="100%" height="166" scrolling="no" frameBorder="no" src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}`} title="SoundCloud" data-testid="podcast-soundcloud-embed" />;
    }
    if (h.includes('anchor.fm') || h.includes('podcasts.apple.com') || h.includes('google.com/podcasts')) {
      return <iframe src={url} width="100%" height="180" frameBorder="0" title="Podcast" data-testid="podcast-generic-embed" />;
    }
  } catch { /* ignore */ }
  return null;
}

export default function PodcastPage() {
  const { id } = useParams();
  const { isKannada } = useLanguage();
  const [pod, setPod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [other, setOther] = useState([]);

  useEffect(() => {
    setLoading(true);
    getPodcast(id).then(r => setPod(r?.data || null)).catch(() => setPod(null)).finally(() => setLoading(false));
    getPodcasts({ limit: 10, status: 'published' }).then(r => setOther((r?.data?.podcasts || []).filter(p => p.podcast_id !== id))).catch(() => {});
  }, [id]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center" data-testid="podcast-loading"><div className="w-8 h-8 border-3 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!pod) return <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="podcast-not-found"><p className="text-gray-500">Podcast not found</p><Link to="/" className="text-red-600 mt-4 inline-block">Back to home</Link></div>;

  const title = isKannada ? (pod.title_kn || pod.title) : pod.title;
  const desc = isKannada ? (pod.description_kn || pod.description) : pod.description;
  const embedNode = pod.embed_code ? (
    <div className="w-full" dangerouslySetInnerHTML={{ __html: sanitizeEmbed(pod.embed_code) }} data-testid="podcast-embed-code" />
  ) : pod.embed_url ? (
    <EmbedFromUrl url={pod.embed_url} />
  ) : pod.audio_url ? (
    <audio controls className="w-full" data-testid="podcast-audio-player"><source src={pod.audio_url} /></audio>
  ) : null;

  return (
    <div className="bg-[#f8f8f8] min-h-screen" data-testid="podcast-page">
      <div className="max-w-[1100px] mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-red-600 mb-4" data-testid="podcast-back">
          <ChevronLeft className="w-4 h-4" /> {isKannada ? 'ಮುಖಪುಟಕ್ಕೆ ಹಿಂದಿರುಗಿ' : 'Back to Home'}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {pod.thumbnail ? (
                <img src={pod.thumbnail} alt="" className="w-full aspect-[16/9] object-cover" />
              ) : (
                <div className="w-full aspect-[16/9] bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center"><Headphones className="w-20 h-20 text-white/70" /></div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-2 text-xs font-bold text-purple-600 mb-2">
                  <Headphones className="w-3.5 h-3.5" /> {isKannada ? 'ಪಾಡ್‌ಕಾಸ್ಟ್' : 'PODCAST'}
                  {pod.duration && <span className="text-gray-400">•</span>}
                  {pod.duration && <span className="text-gray-500">{pod.duration}</span>}
                </div>
                <h1 className="text-2xl md:text-3xl font-black text-[#1a1a1a] font-kannada leading-tight" data-testid="podcast-title">{title}</h1>
                {desc && <p className="mt-4 text-gray-700 whitespace-pre-wrap font-kannada" data-testid="podcast-description">{desc}</p>}
                {embedNode && <div className="mt-6">{embedNode}</div>}
                {!embedNode && <p className="mt-6 text-sm text-gray-400">{isKannada ? 'ಯಾವುದೇ ಮಾಧ್ಯಮ ಲಭ್ಯವಿಲ್ಲ' : 'No media available for this episode.'}</p>}
              </div>
            </div>
          </div>

          <aside className="space-y-3" data-testid="podcast-sidebar">
            <h3 className="text-sm font-bold text-[#1a1a1a] uppercase tracking-wider">{isKannada ? 'ಇನ್ನಷ್ಟು ಪಾಡ್‌ಕಾಸ್ಟ್‌ಗಳು' : 'More Episodes'}</h3>
            {other.slice(0, 8).map(p => (
              <Link key={p.podcast_id} to={`/podcast/${p.podcast_id}`} className="flex gap-3 p-2 rounded-lg bg-white border border-gray-100 hover:border-purple-300 transition-colors" data-testid={`podcast-related-${p.podcast_id}`}>
                {p.thumbnail ? <img src={p.thumbnail} alt="" className="w-16 h-16 rounded object-cover shrink-0" /> : <div className="w-16 h-16 rounded bg-purple-100 flex items-center justify-center shrink-0"><Play className="w-5 h-5 text-purple-500" /></div>}
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-bold text-[#1a1a1a] line-clamp-2 font-kannada">{isKannada ? (p.title_kn || p.title) : p.title}</p>
                  {p.duration && <span className="text-[11px] text-gray-400">{p.duration}</span>}
                </div>
              </Link>
            ))}
            {other.length === 0 && <p className="text-sm text-gray-400">{isKannada ? 'ಯಾವುದೇ ಇತರ ಸಂಚಿಕೆಗಳಿಲ್ಲ' : 'No other episodes.'}</p>}
          </aside>
        </div>
      </div>
    </div>
  );
}
